
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAdminStats = (isAdmin: boolean) => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalParkingSpots: 0,
    totalBookings: 0,
    activeBookings: 0,
    completedBookings: 0,
    cancelledBookings: 0,
    pendingApprovals: 0,
    averageRating: 0,
    revenue: 0,
  });
  const { toast } = useToast();

  useEffect(() => {
    const fetchStats = async () => {
      if (!isAdmin) return;

      try {
        const [
          { count: usersCount },
          { count: parkingSpotsCount },
          { count: bookingsCount },
          { count: activeBookingsCount },
          { count: completedBookingsCount },
          { count: cancelledBookingsCount },
          { count: pendingApprovalCount },
          // Get average rating
          { data: ratingData },
          // Get total revenue using our new function
          { data: revenueData, error: revenueError },
        ] = await Promise.all([
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.from('parking_spots').select('*', { count: 'exact', head: true }),
          supabase.from('bookings').select('*', { count: 'exact', head: true }),
          supabase.from('bookings').select('*', { count: 'exact', head: true })
            .eq('status', 'active'),
          supabase.from('bookings').select('*', { count: 'exact', head: true })
            .eq('status', 'completed'),
          supabase.from('bookings').select('*', { count: 'exact', head: true })
            .eq('status', 'cancelled'),
          supabase.from('parking_spots').select('*', { count: 'exact', head: true })
            .eq('is_available', false),
          supabase.from('feedback').select('rating'),
          supabase.rpc('get_total_revenue'),
        ]);

        if (revenueError) throw revenueError;

        // Calculate average rating
        let avgRating = 0;
        if (ratingData && ratingData.length > 0) {
          const sum = ratingData.reduce((acc, curr) => acc + curr.rating, 0);
          avgRating = sum / ratingData.length;
        }

        // Get total revenue from the function
        const revenue = revenueData && revenueData.length > 0 ? revenueData[0].total_revenue : 0;

        setStats({
          totalUsers: usersCount || 0,
          totalParkingSpots: parkingSpotsCount || 0,
          totalBookings: bookingsCount || 0,
          activeBookings: activeBookingsCount || 0,
          completedBookings: completedBookingsCount || 0,
          cancelledBookings: cancelledBookingsCount || 0,
          pendingApprovals: pendingApprovalCount || 0,
          averageRating: avgRating,
          revenue: revenue,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        toast({
          title: "Error",
          description: "Failed to load dashboard statistics",
          variant: "destructive",
        });
      }
    };

    fetchStats();
  }, [isAdmin, toast]);

  return stats;
};
